// Subscription Types and Plans

export type SubscriptionPlan = 'free' | 'starter' | 'growth' | 'professional' | 'enterprise';
export type BillingCycle = 'monthly' | 'yearly';

export interface PlanLimits {
  google: boolean;
  marketplaces: number;
  websites: number;
  adPlatforms: number;
  reportingAnalytics: number;
  accounting: number;
  aiChat: boolean;
  aiProfile: boolean;
  notifications: boolean;
  reports: boolean;
}

export interface SubscriptionPlanDetails {
  id: SubscriptionPlan;
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  yearlyDiscount: number;
  limits: PlanLimits;
  features: string[];
  recommended?: boolean;
  badge?: string;
}

export interface UserSubscription {
  plan: SubscriptionPlan;
  billingCycle: BillingCycle;
  trialStartDate: Date;
  trialEndDate: Date;
  isTrialActive: boolean;
  isSubscribed: boolean;
  subscriptionStartDate?: Date;
  subscriptionEndDate?: Date;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
}

// Plan definitions
export const SUBSCRIPTION_PLANS: SubscriptionPlanDetails[] = [
  {
    id: 'starter',
    name: 'Başlangıç',
    description: 'Küçük işletmeler için ideal başlangıç paketi',
    monthlyPrice: 14.90,
    yearlyPrice: 143.04, // 14.90 * 12 * 0.8
    yearlyDiscount: 20,
    limits: {
      google: true,
      marketplaces: 1,
      websites: 1,
      adPlatforms: 1,
      reportingAnalytics: 0,
      accounting: 0,
      aiChat: true,
      aiProfile: true,
      notifications: true,
      reports: false
    },
    features: [
      'Google Analytics entegrasyonu',
      '1 Pazaryeri bağlantısı',
      '1 Web sitesi bağlantısı',
      '1 Reklam platformu',
      'AI Asistan (sınırlı)',
      'Temel bildirimler',
      'E-posta desteği'
    ]
  },
  {
    id: 'growth',
    name: 'Büyüme',
    description: 'Büyüyen e-ticaret işletmeleri için',
    monthlyPrice: 19.90,
    yearlyPrice: 191.04, // 19.90 * 12 * 0.8
    yearlyDiscount: 20,
    limits: {
      google: true,
      marketplaces: 3,
      websites: 2,
      adPlatforms: 2,
      reportingAnalytics: 2,
      accounting: 1,
      aiChat: true,
      aiProfile: true,
      notifications: true,
      reports: true
    },
    features: [
      'Google Analytics entegrasyonu',
      '3 Pazaryeri bağlantısı',
      '2 Web sitesi bağlantısı',
      '2 Reklam platformu',
      '2 Raporlama & Analitik',
      '1 Muhasebe entegrasyonu',
      'AI Asistan (tam erişim)',
      'Gelişmiş bildirimler',
      'Öncelikli destek'
    ]
  },
  {
    id: 'professional',
    name: 'Profesyonel',
    description: 'Tam kapsamlı e-ticaret yönetimi',
    monthlyPrice: 24.90,
    yearlyPrice: 239.04, // 24.90 * 12 * 0.8
    yearlyDiscount: 20,
    recommended: true,
    badge: 'En Popüler',
    limits: {
      google: true,
      marketplaces: 5,
      websites: 3,
      adPlatforms: 3,
      reportingAnalytics: 3,
      accounting: 2,
      aiChat: true,
      aiProfile: true,
      notifications: true,
      reports: true
    },
    features: [
      'Google Analytics entegrasyonu',
      '5 Pazaryeri bağlantısı',
      '3 Web sitesi bağlantısı',
      '3 Reklam platformu',
      '3 Raporlama & Analitik',
      '2 Muhasebe entegrasyonu',
      'AI Asistan (premium)',
      'Gelişmiş raporlama',
      'Tüm bildirimler',
      'PDF & Excel export',
      '7/24 Öncelikli destek'
    ]
  },
  {
    id: 'enterprise',
    name: 'Kurumsal',
    description: 'Sınırsız erişim ve özel destek',
    monthlyPrice: 34.90,
    yearlyPrice: 335.04, // 34.90 * 12 * 0.8
    yearlyDiscount: 20,
    badge: 'Sınırsız',
    limits: {
      google: true,
      marketplaces: -1, // -1 = unlimited
      websites: -1,
      adPlatforms: -1,
      reportingAnalytics: -1,
      accounting: -1,
      aiChat: true,
      aiProfile: true,
      notifications: true,
      reports: true
    },
    features: [
      'Sınırsız pazaryeri bağlantısı',
      'Sınırsız web sitesi bağlantısı',
      'Sınırsız reklam platformu',
      'Sınırsız raporlama',
      'Sınırsız muhasebe entegrasyonu',
      'API erişimi',
      'Özel entegrasyonlar',
      'Dedicated account manager',
      'SLA garantisi',
      'Özel eğitim ve onboarding'
    ]
  }
];

// Trial duration in days
export const TRIAL_DURATION_DAYS = 30;

// Helper functions
export const getPlanById = (id: SubscriptionPlan): SubscriptionPlanDetails | undefined => {
  return SUBSCRIPTION_PLANS.find(plan => plan.id === id);
};

export const formatPrice = (price: number, cycle: BillingCycle = 'monthly'): string => {
  if (cycle === 'yearly') {
    const monthlyEquivalent = price / 12;
    return `$${monthlyEquivalent.toFixed(2)}`;
  }
  return `$${price.toFixed(2)}`;
};

export const calculateTrialEndDate = (startDate: Date): Date => {
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + TRIAL_DURATION_DAYS);
  return endDate;
};

export const isTrialExpired = (trialEndDate: Date): boolean => {
  return new Date() > trialEndDate;
};

export const getDaysRemaining = (trialEndDate: Date): number => {
  const now = new Date();
  const diff = trialEndDate.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

export const canConnectPlatform = (
  currentConnections: { category: string; count: number }[],
  category: string,
  planLimits: PlanLimits
): { allowed: boolean; limit: number; current: number } => {
  const categoryMapping: Record<string, keyof PlanLimits> = {
    'Pazaryerleri': 'marketplaces',
    'E-Ticaret Platformları': 'websites',
    'Reklam Platformları': 'adPlatforms',
    'Raporlama ve Analitik': 'reportingAnalytics',
    'Muhasebe ve ERP': 'accounting'
  };

  const limitKey = categoryMapping[category];
  if (!limitKey) return { allowed: true, limit: -1, current: 0 };

  const limit = planLimits[limitKey] as number;
  const current = currentConnections.find(c => c.category === category)?.count || 0;

  // -1 means unlimited
  if (limit === -1) return { allowed: true, limit: -1, current };

  return {
    allowed: current < limit,
    limit,
    current
  };
};
